import { ReactNode, useEffect, useState } from "react";
import { createPortal } from "react-dom";

type SlidePosition = "left" | "right" | "top" | "bottom";

export default function Drawer({
  children,
  isOpen,
  position = "right",
  size = "50%",
  onClose,
  transitionMs = 500,
  className,
}: {
  children: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  position?: SlidePosition;
  size?: string;
  transitionMs?: number;
  className?: string;
}) {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let timeoutId: number | undefined;

    if (isOpen) {
      setMounted(true);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
    } else {
      setVisible(false);

      timeoutId = window.setTimeout(() => setMounted(false), transitionMs);
    }

    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [isOpen, transitionMs]);

  if (!mounted) return null;

  const baseClasses =
    "fixed bg-white shadow-2xl transform transition-transform duration-500 ease-in-out z-50 overflow-hidden";

  const positionClasses = {
    right: `top-0 right-0 h-full rounded-l-2xl`,
    left: `top-0 left-0 h-full rounded-r-2xl`,
    bottom: `bottom-0 left-0 w-full rounded-t-2xl`,
    top: `top-0 left-0 w-full rounded-b-2xl`,
  }[position];

  const sizeStyle =
    position === "right" || position === "left"
      ? { width: size }
      : { height: size };

  const transformClasses = {
    right: visible ? "translate-x-0" : "translate-x-full",
    left: visible ? "translate-x-0" : "-translate-x-full",
    bottom: visible ? "translate-y-0" : "translate-y-full",
    top: visible ? "translate-y-0" : "-translate-y-full",
  }[position];

  // Backdrop opacity driven by `visible` for smooth fade
  const backdropOpacity = visible
    ? "opacity-100"
    : "opacity-0 pointer-events-none";

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/20 backdrop-blur-sm transition-opacity duration-${transitionMs} ${backdropOpacity}`}
        onClick={onClose}
        aria-hidden
      />

      {/* Drawer */}
      <div
        className={`${baseClasses} ${positionClasses} ${transformClasses} ${
          className ? className : ""
        }`}
        style={{ transitionDuration: `${transitionMs}ms`, ...sizeStyle }}
      >
        {children}
      </div>
    </>,
    document.getElementById("drawer")!
  );
}
